import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface TocItem {
  id: string;
  labelKey: string;
  children?: { id: string; labelKey: string }[];
}

interface TableOfContentsProps {
  items: TocItem[];
  activeSection: string;
  onSelect?: (id: string) => void;
}

export function TableOfContents({ items, activeSection, onSelect }: TableOfContentsProps) {
  const { t } = useTranslation('help');

  function handleClick(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      history.replaceState(null, '', `#${id}`);
    }
    onSelect?.(id);
  }

  return (
    <nav className="space-y-1">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
        {t('tocTitle')}
      </h3>
      {items.map((item) => (
        <div key={item.id}>
          <button
            type="button"
            onClick={() => handleClick(item.id)}
            className={cn(
              'block w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors',
              activeSection === item.id
                ? 'text-brand-rose font-semibold bg-brand-rose/[0.08]'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            )}
          >
            {t(item.labelKey)}
          </button>
          {item.children?.map((child) => (
            <button
              key={child.id}
              type="button"
              onClick={() => handleClick(child.id)}
              className={cn(
                'block w-full text-left text-xs px-3 py-1 pl-6 rounded-md transition-colors',
                activeSection === child.id
                  ? 'text-brand-rose font-semibold'
                  : 'text-slate-400 hover:text-slate-600'
              )}
            >
              {t(child.labelKey)}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}
